/**
 * Chokh, the first-party analytics tracker (`AN-INT01`, ADR-0069).
 *
 * The script is `a.js` from `analytics.progsity.io`, served through this site's
 * own origin, so no request leaves for a second hostname and a content blocker
 * has no third-party name to match on. `next.config.mjs` rewrites `/_pa/a.js`
 * to `NEXT_PUBLIC_CHOKH_URL`; the collect path is `app/_pa/collect/route.ts`
 * rather than a rewrite, because a rewrite is fetched by Vercel and would store
 * every visitor as Vercel (`AN-PRX01`). The script tag lives in the root
 * layout; everything here is the page's side of the `pa()` command interface it
 * installs.
 *
 * Nothing is sent when `NEXT_PUBLIC_CHOKH_SITE` is unset: the tag is not
 * rendered, the tracker never boots, and every call below is a no-op. The
 * site's own domain allowlist (`progsity.io`, `www.progsity.io`) is what keeps
 * a preview deployment out of the reports, so there is no environment check
 * here beyond the missing key.
 */
export const CHOKH_SITE = process.env.NEXT_PUBLIC_CHOKH_SITE ?? "";

/** The first-party paths: `a.js` a rewrite, `collect` this app's own route. */
export const CHOKH_SCRIPT_SRC = "/_pa/a.js";
export const CHOKH_API_BASE = "/_pa";

/**
 * The events this app sends. A union rather than a string so a typo is a build
 * error and the set stays the one the ticket named.
 *
 * Two of the eight are not in it, and both for the same reason: they are sent
 * server-side in `AN-INT02` and Chokh has no event-id deduplication, so a
 * browser copy would count the same fact twice. `order_paid` never had a
 * browser event, because money is confirmed by a gateway callback, a webhook or
 * an administrator, none of which is a page. `signup` did, from the register
 * form, and it came out when the server's went in: the OAuth and
 * contest-conversion signups have no page to fire from, so counting all three
 * in one place was the only way to have one series.
 */
export type ChokhEventName =
  | "course_view"
  | "checkout_start"
  | "quiz_start"
  | "quiz_submit"
  | "playground_run"
  | "contest_register"
  // A page that matched nothing (`AN-INT05`). A browser event and not a server
  // one, the other way round from `signup`: on a soft navigation the server
  // only renders a payload, which may be a prefetch nobody opened or may never
  // be asked for again once the router has cached it, so only the browser knows
  // a navigation actually landed on nothing. Chokh's Pages report counts this
  // name as a 404 wherever a pageview carries no status.
  | "404";

export type ChokhEventProps = Record<string, string | number | boolean | undefined>;

type PaArgs = [string, ...unknown[]];
type Pa = ((...args: PaArgs) => void) & { q?: PaArgs[] };

declare global {
  interface Window {
    pa?: Pa;
  }
}

/**
 * `a.js` is deferred, so a call can reach this before the tracker boots. The
 * tracker replays whatever it finds in `pa.q` on boot, so an early call is
 * queued rather than dropped.
 */
function pa(...args: PaArgs): void {
  const existing = window.pa;
  if (existing !== undefined) {
    existing(...args);
    return;
  }
  const stub = ((...queued: PaArgs) => {
    (stub.q = stub.q ?? []).push(queued);
  }) as Pa;
  window.pa = stub;
  stub(...args);
}

function enabled(): boolean {
  return typeof window !== "undefined" && CHOKH_SITE !== "";
}

/** A product event. Properties reach the reports as strings; keep them small and non-identifying. */
export function trackChokh(name: ChokhEventName, props?: ChokhEventProps): void {
  if (!enabled()) return;
  pa("event", name, props);
}

/**
 * Link this visitor to an account.
 *
 * The fourth argument is the site's proof of `userId`, an HMAC issued by the
 * server from the site's `identifySecret`. `ps_web` runs with
 * `allowUnsignedIdentify` off (`docs/08`, the two sites), so an identify
 * without one is refused and that visitor's events are still collected, just
 * anonymously. The auth payload starts carrying the signature in `AN-INT02`;
 * until then it is `undefined` and this call is the wiring, not the identity.
 */
export function identifyChokh(userId: string, signature?: string): void {
  if (!enabled()) return;
  pa("identify", userId, undefined, signature);
}

/**
 * Forget the account and, in persistent mode, the stored visitor id, so the
 * next person on a shared browser starts as a new visitor.
 */
export function resetChokh(): void {
  if (!enabled()) return;
  pa("reset");
}
